import React from 'react';
import { Image, Link, Text, View } from '@react-pdf/renderer';
import { getScenarioStatusName, getRiskSourcesName, getRiskEventsName, getRiskConsequencesName } from 'src/api/types';
import { isUndefined, numeralFormat, getCurrencySymbol } from 'src/utils/misc';
import { pdfStyles } from '../pdfStyles';
import { NoData } from './NoData';
import { getExternalPath } from '../util';
import { extLinkImg } from '../imgData';
import { ColorSet } from 'src/redux/reducers/theme';
import { ScenarioTablePDF } from '../types';

type RiskManagementPlanGeneric = {
  id?: string;
  modifiers?: Array<{
    type?: string | null;
    isPercentage?: boolean | null;
    consequenceLower?: number | null;
    consequenceUpper?: number | null;
    frequencyReduction?: number | null;
    totalPolicyCover?: number | null;
    excess?: number | null;
    note?: string | null;
  } | null> | null;
  scenario?: {
    id: string;
  } | null;
};

interface ScenariosTableProps {
  data: ScenarioTablePDF[];
  colors: ColorSet;
  riskManagementPlans?: RiskManagementPlanGeneric[];
}

export const ScenariosTable: React.FC<ScenariosTableProps> = ({ data, colors, riskManagementPlans }) => {
  const symbol = getCurrencySymbol();

  return (
    <View>
      <Text style={pdfStyles.label}>RISK SCENARIOS</Text>
      <View style={pdfStyles.spacer4} />
      {data.length ? (
        <>
          <View
            wrap={false}
            break={false}
            style={{
              height: 24,
              border: `1px solid ${colors.cflowerBlue}`,
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <View style={{ ...pdfStyles.tableCell, flex: 0.96 }}>
              <Text style={pdfStyles.tableHeader}>Name</Text>
            </View>
            <View style={{ ...pdfStyles.tableCell, flex: 0.384 }}>
              <Text style={pdfStyles.tableHeader}>Status</Text>
            </View>
            <View style={{ ...pdfStyles.tableCell, flex: 0.8 }}>
              <Text style={pdfStyles.tableHeader}>Risk Owner</Text>
            </View>
            <View style={{ ...pdfStyles.tableCell, flex: 0.6 }}>
              <Text style={pdfStyles.tableHeader}>Sources</Text>
            </View>
            <View style={{ ...pdfStyles.tableCell, flex: 0.6 }}>
              <Text style={pdfStyles.tableHeader}>Events</Text>
            </View>
            <View style={pdfStyles.tableCell}>
              <Text style={pdfStyles.tableHeader}>Consequences</Text>
            </View>
            <View style={{ ...pdfStyles.tableCell, flex: 0.9 }}>
              <Text style={pdfStyles.tableHeader}>Expected Loss</Text>
            </View>
          </View>
          {data.map((el, idx) => (
            <React.Fragment key={`scenario-${el?.id}`}>
              <View
                wrap={false}
                break={false}
                style={{
                  minHeight: 24,
                  border: `1px solid ${colors.cflowerBlue}`,
                  display: 'flex',
                  flexDirection: 'row',
                  marginTop: -1,
                }}
              >
                <View style={{ ...pdfStyles.tableCell, flex: 0.96 }}>
                  <Link
                    src={getExternalPath(`risk-assessments/${el?.assessment?.id}/scenario/${el?.id}`)}
                    style={{
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      flexDirection: 'row',
                      textDecoration: 'none',
                    }}
                  >
                    <Text style={{ ...pdfStyles.tableValue, textDecoration: 'underline', marginRight: 6 }}>
                      {el?.name || '-'}
                    </Text>
                    {/* <Image src={extLinkImg} style={{ width: 8, height: 8 }} /> */}
                  </Link>
                </View>
                <View style={{ ...pdfStyles.tableCell, flex: 0.384 }}>
                  <Text style={pdfStyles.tableValue}>{getScenarioStatusName(el?.status) || '-'}</Text>
                </View>
                <View style={{ ...pdfStyles.tableCell, flex: 0.8 }}>
                  <Text style={{ ...pdfStyles.tableValue }}>
                    {(el?.riskOwner?.text || el?.riskOwner?.email || '-').split('')}
                  </Text>
                </View>
                <View style={{ ...pdfStyles.tableCell, flex: 0.6 }}>
                  <Text style={pdfStyles.tableValue}>
                    {!isUndefined(el?.source) ? getRiskSourcesName(el?.source) : '-'}
                  </Text>
                </View>
                <View style={{ ...pdfStyles.tableCell, flex: 0.6 }}>
                  <Text style={pdfStyles.tableValue}>
                    {!isUndefined(el?.event) ? getRiskEventsName(el?.event) : '-'}
                  </Text>
                </View>
                <View style={pdfStyles.tableCell}>
                  <Text style={pdfStyles.tableValue}>
                    {!isUndefined(el?.consequence) ? getRiskConsequencesName(el?.consequence) : '-'}
                  </Text>
                </View>
                <View style={{ ...pdfStyles.tableCell, flex: 0.9 }}>
                  <Text style={{ ...pdfStyles.tableValue, fontWeight: 600, fontSize: 10 }}>
                    {el?.liveRisk?.expectedLoss ? `${numeralFormat(el.liveRisk.expectedLoss)}` : '-'}
                  </Text>
                </View>
              </View>

              {/* {(() => {
                const riskManagementPlan = riskManagementPlans?.find((rmp) => rmp.scenario?.id === el?.id);
                const modifiers = riskManagementPlan?.modifiers || [];

                if (modifiers.length === 0) return null;

                const sortedModifiers = modifiers.slice().sort((a, b) => {
                  if (a?.type === 'NOTE_TYPE') return 1;
                  if (b?.type === 'NOTE_TYPE') return -1;
                  return (a?.type || '').localeCompare(b?.type || '');
                });

                return sortedModifiers.map((modifier, modifierIdx) => {
                  let modifierText = '';

                  switch (modifier?.type) {
                    case 'TREAT_REDUCE_CONSEQUENCE':
                      modifierText = `Reduces consequence by ${
                        modifier.isPercentage
                          ? `${modifier.consequenceLower}%`
                          : `${symbol}${modifier.consequenceLower}`
                      } (Lower) and ${modifier.isPercentage ? `${modifier.consequenceUpper}%` : `${symbol}${modifier.consequenceUpper}`} (Upper)`;
                      break;
                    case 'TREAT_REDUCE_FREQUENCY':
                      modifierText = `Reduces frequency by ${modifier.isPercentage ? `${modifier.frequencyReduction}%` : `#${modifier.frequencyReduction}`}`;
                      break;
                    case 'NOTE_TYPE':
                      modifierText = `Note: ${modifier.note}`;
                      break;
                    case 'TRANSFER_LIMIT':
                      modifierText = `Reduces risk with a total policy cover of ${symbol}${modifier.totalPolicyCover?.toLocaleString()} and an excess of ${symbol}${modifier.excess?.toLocaleString()}`;
                      break;
                    default:
                      modifierText = 'Unknown modifier type';
                  }

                  return (
                    <View
                      key={`modifier-${modifierIdx}`}
                      style={{
                        backgroundColor: colors.skeletonHighlight,
                        padding: 6,
                        borderLeft: `1px solid ${colors.cflowerBlue}`,
                        borderRight: `1px solid ${colors.cflowerBlue}`,
                        borderBottom:
                          modifierIdx === sortedModifiers.length - 1 ? `1px solid ${colors.cflowerBlue}` : 'none',
                      }}
                    >
                      <Text style={pdfStyles.tableValue}>{modifierText}</Text>
                    </View>
                  );
                });
              })()} */}
            </React.Fragment>
          ))}
        </>
      ) : (
        <NoData colors={colors} />
      )}
    </View>
  );
};
